import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user) {
        checkAuthorization(user);
      } else {
        setIsAuthorized(false);
        setUserRole('');
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const checkAuthorization = async (user) => {
    try {
      const q = query(collection(db, 'authorizedUsers'), where('email', '==', user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        setUserRole(userData.role);

        const now = new Date();
        const expirationDate = userData.expiresAt.toDate();

        const authorizedRoles = ['employee', 'closed', 'admin', 'owner'];
        if (now < expirationDate && authorizedRoles.includes(userData.role)) {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      } else {
        setIsAuthorized(false);
        setUserRole('public');
      }
    } catch (error) {
      console.error("Error checking authorization:", error);
      setIsAuthorized(false);
      setUserRole('');
    }
    setLoading(false);
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setIsAuthorized(false);
      setUserRole('');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const value = {
    user,
    loading,
    signOut,
    isAuthorized,
    userRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
